<script setup lang="ts">
  // V2 confirmation dialog, opened through useConfirmDialog().confirm(), which
  // resolves true (accept) or false (cancel, Esc). The legacy
  // useModal().confirm renders a Vuetify dialog that cannot draw the V2
  // button roles (red soft for destructive answers).
  import { onMounted, ref } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import PosUiButton from './PosUiButton.vue';

  const props = withDefaults(
    defineProps<{
      /** The vue-js-modal name it was opened with. */
      dialogName: string;
      title: string;
      message: string;
      acceptText: string;
      cancelText?: string;
      tone?: 'primary' | 'danger';
    }>(),
    { cancelText: undefined, tone: 'primary' }
  );

  const modals = useModal();
  const root = ref<HTMLElement | null>(null);

  // Keyboard users land on the safe answer; Esc then cancels.
  onMounted(() => {
    root.value
      ?.querySelector<HTMLElement>('.confirm-cancel')
      ?.focus({ preventScroll: true });
  });

  function answer(accepted: boolean) {
    // Closes and unmounts the dialog: nothing may run after this.
    modals.dialogSubmit(props.dialogName, accepted);
  }
</script>

<template>
  <div
    ref="root"
    class="pos-ui-confirm-dialog"
    role="alertdialog"
    :aria-label="title"
    @keydown.esc.stop="answer(false)"
  >
    <h2 class="confirm-title">{{ title }}</h2>
    <p class="confirm-message">{{ message }}</p>
    <div class="confirm-actions">
      <PosUiButton
        class="confirm-cancel"
        color="neutral"
        variant="ghost"
        @click="answer(false)"
      >
        {{ cancelText || $t('cancel') }}
      </PosUiButton>
      <PosUiButton
        class="confirm-accept"
        :color="tone === 'danger' ? 'error' : 'primary'"
        :variant="tone === 'danger' ? 'soft' : 'solid'"
        @click="answer(true)"
      >
        {{ acceptText }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // A column: the modal wrapper (.dialog--height-limit > div) is a flex row.
  .pos-ui-confirm-dialog {
    @apply tw-flex tw-flex-col tw-bg-surface tw-px-5 tw-pb-4 tw-pt-5;

    .confirm-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-leading-snug tw-text-neutral-900;
    }

    .confirm-message {
      @apply tw-mb-0 tw-mt-2 tw-whitespace-pre-line tw-text-sm tw-leading-relaxed tw-text-neutral-600;
    }

    // Margins, not flex gap (old POS hardware, Chrome < 84): 8px around
    // the buttons, absorbed by the negative margin, so long labels wrap
    // with room between the rows too.
    .confirm-actions {
      @apply tw--m-1 tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-end;

      .confirm-cancel,
      .confirm-accept {
        @apply tw-m-1;
      }
    }
  }
</style>
